import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {OVERLAP_WINDOW} from 'bulk-workflows/assembly/BulkAssemblyConstants';
import {BulkAssemblyPreferences} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import preferences from 'Preferences';
import ErrorMessage from 'ui-library/ErrorMessage';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import Modal from 'ui-library/modals/Modal';

const getMaxOverlap = (minOverlap: number | null): string =>
  minOverlap == null ? '??' : String(minOverlap + OVERLAP_WINDOW);

export enum GibsonInputKeys {
  tmAnneal = 'tmAnneal',
  tmWhole = 'tmWhole',
  tmDiff = 'tmDiff',
  minOverlap = 'minOverlap',
}

interface ParametersInfo {
  paramName: string;
  paramDescription: string;
  minValue: number;
  maxValue: number;
}

export const parametersInfo: {[key in GibsonInputKeys]: ParametersInfo} = {
  tmAnneal: {
    paramName: "Min. melting temp. for primer's binding bases",
    paramDescription: "The minimum melting temperature for the primer's binding bases",
    minValue: -100,
    maxValue: 200,
  },
  tmWhole: {
    paramName: 'Min. melting temp. for whole primer',
    paramDescription: 'The minimum melting temperature for the whole primer',
    minValue: -100,
    maxValue: 200,
  },
  tmDiff: {
    paramName: 'Max. melting temp. difference for primer pair',
    paramDescription: 'The minimum melting temperature difference for the primer pair',
    minValue: -100,
    maxValue: 200,
  },
  minOverlap: {
    paramName: 'Length of homology/binding regions',
    paramDescription: 'The minimum length of the homology/binding region',
    minValue: 10,
    maxValue: 100 - OVERLAP_WINDOW,
  },
};

interface RawGibsonParameters {
  tmAnneal: number | null;
  tmWhole: number | null;
  tmDiff: number | null;
  minOverlap: number | null;
}

interface FinalizedGibsonParameters {
  tmAnneal: number;
  tmWhole: number;
  tmDiff: number;
  minOverlap: number;
}

function findErrors(gibsonParameters: RawGibsonParameters): {[key in GibsonInputKeys]: string | null} {
  const errors: {[key in GibsonInputKeys]: string | null} = {
    tmAnneal: null,
    tmWhole: null,
    tmDiff: null,
    minOverlap: null,
  };
  Object.values(GibsonInputKeys).forEach((key) => {
    if (gibsonParameters[key] == null) {
      errors[key] = `${parametersInfo[key].paramDescription} must be a valid ${
        key === 'minOverlap' ? 'integer' : 'number'
      } between ${parametersInfo[key].minValue} and ${parametersInfo[key].maxValue}.`;
    }
  });
  return errors;
}

interface InputCellProps {
  value: number | null;
  onChange: (numericValue: number | null) => void;
  units: string;
  paramKey: GibsonInputKeys;
}

function InputCell({value, onChange, units, paramKey}: InputCellProps): JSX.Element {
  return (
    <div className={css(styles.cell)}>
      <FieldLabel label={parametersInfo[paramKey].paramName} />
      <div className={css(styles.inputSection)}>
        <NumericInput
          min={parametersInfo[paramKey].minValue}
          max={parametersInfo[paramKey].maxValue}
          value={value}
          className={css(styles.input)}
          onChange={(e) => {
            onChange(e.target.numericValue);
          }}
          required={true}
          validateInteger={paramKey === 'minOverlap'}
        />
        <div className={css(styles.inputText)}>{units}</div>
      </div>
    </div>
  );
}

interface Props {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function GibsonParametersModal(props: Props): JSX.Element {
  const [gibsonParameters, setGibsonParameters] = useState<RawGibsonParameters>(
    preferences.get(BulkAssemblyPreferences.GIBSON_PARAMETERS)
  );
  const {onRequestClose, onRequestSubmit} = props;
  return (
    <Modal
      title="Gibson Primer Settings"
      buttons={[
        {
          text: 'Save',
          submit: true,
          disabled: Object.values(findErrors(gibsonParameters)).some((e) => e != null),
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        assert(
          Object.values(gibsonParameters).every((p) => p != null),
          'Expected to receive non-null Gibson parameters.'
        );
        preferences.set(BulkAssemblyPreferences.GIBSON_PARAMETERS, {
          ...(gibsonParameters as FinalizedGibsonParameters),
          maxOverlap: gibsonParameters.minOverlap! + OVERLAP_WINDOW,
        });
        onRequestSubmit();
      }}
      large={true}
    >
      <div className={css(styles.row)}>
        <InputCell
          value={gibsonParameters.tmAnneal}
          onChange={(numericValue: number | null) => {
            setGibsonParameters({...gibsonParameters, tmAnneal: numericValue});
          }}
          units={'\u00B0 C'}
          paramKey={GibsonInputKeys.tmAnneal}
        />
        <InputCell
          value={gibsonParameters.tmWhole}
          onChange={(numericValue: number | null) => {
            setGibsonParameters({...gibsonParameters, tmWhole: numericValue});
          }}
          units={'\u00B0 C'}
          paramKey={GibsonInputKeys.tmWhole}
        />
      </div>
      <div className={css(styles.row)}>
        <InputCell
          value={gibsonParameters.tmDiff}
          onChange={(numericValue: number | null) => {
            setGibsonParameters({...gibsonParameters, tmDiff: numericValue});
          }}
          units={'\u00B0 C'}
          paramKey={GibsonInputKeys.tmDiff}
        />
        <InputCell
          value={gibsonParameters.minOverlap}
          onChange={(numericValue: number | null) => {
            setGibsonParameters({
              ...gibsonParameters,
              minOverlap: numericValue,
            });
          }}
          units={`to ${getMaxOverlap(gibsonParameters.minOverlap)} bases`}
          paramKey={GibsonInputKeys.minOverlap}
        />
      </div>
      {Object.values(findErrors(gibsonParameters))
        .filter((e) => e != null)
        .map((e, i) => (
          <ErrorMessage key={i} error={e} />
        ))}
    </Modal>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
  },
  cell: {
    margin: '10px',
    flex: '5',
  },
  inputSection: {
    display: 'flex',
  },
  input: {
    width: '50px',
    margin: '5px',
  },
  inputText: {
    marginLeft: '0px',
    marginTop: 'auto',
    marginBottom: 'auto',
  },
});
